import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  CalendarDays,
  HelpCircle,
  LayoutGrid,
  MessageSquare,
  Palette,
  PlusCircle,
  Share2,
  Users,
} from 'lucide-react';
import dbUrl from '../../utils/credentials';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { getResponseCount } from '../../lib/responses';
import { isInvitationOpen } from '../../lib/invitation';
import logo from '../../assets/logo/logo_banco_alimentare.svg';

const steps = [
  {
    icon: PlusCircle,
    title: 'Crea l\'invito',
    description: 'Inserisci nome, descrizione e le informazioni base. Il link verrà generato automaticamente dal nome scelto.',
    color: 'text-brand-orange',
  },
  {
    icon: Palette,
    title: 'Personalizza',
    description: 'Scegli colori, logo, immagine di copertina e i campi da richiedere ai partecipanti (testo, email, telefono, scelta singola o multipla).',
    color: 'text-brand-green',
  },
  {
    icon: Share2,
    title: 'Condividi',
    description: 'Copia il link o scarica il QR code dall\'elenco inviti. Ogni invito ha un URL univoco del tipo /invito?id=nome-evento.',
    color: 'text-brand-info',
  },
  {
    icon: HelpCircle,
    title: 'Raccogli risposte',
    description: 'Visualizza, filtra ed esporta le risposte dalla sezione Risposte. Gli inviti con risposte non possono essere modificati.',
    color: 'text-brand-gold',
  },
  {
    icon: LayoutGrid,
    title: 'Organizza l\'evento',
    description: 'Crea un evento collegato all\'invito: assegna i volontari ai negozi per turno, nomina responsabili e vice, aggiungi note al negozio ed esporta il piano in Excel o PDF.',
    color: 'text-brand-info',
  },
];

const faqs = [
  {
    q: 'Posso modificare un invito dopo aver ricevuto risposte?',
    a: 'No, per garantire l\'integrità dei dati raccolti gli inviti con almeno una risposta non sono modificabili. Puoi duplicare l\'invito e crearne uno nuovo.',
  },
  {
    q: 'Come funziona il link dell\'invito?',
    a: 'Il link viene generato dal nome dell\'invito (es. "Cena Solidale" → /invito?id=cena-solidale). Se il nome esiste già, viene aggiunto un suffisso numerico.',
  },
  {
    q: 'Quali tipi di campo posso aggiungere?',
    a: 'Testo, numero, email, telefono, data, consenso (checkbox) e scelta singola/multipla con limite posti opzionale. Puoi riordinare i campi a piacimento.',
  },
  {
    q: 'Come organizzo i turni di una raccolta alimentare?',
    a: 'Dopo aver raccolto le iscrizioni, vai in Eventi e crea un nuovo evento collegato all\'invito. I turni vengono letti dalle disponibilità indicate nei moduli: potrai trascinare i volontari nei negozi e esportare il piano.',
  },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

function CountUp({ value, duration = 0.55, decimals = 0, className }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const target = Number(value) || 0;
    if (target === 0) {
      setDisplay(0);
      return undefined;
    }

    const start = performance.now();
    const durationMs = duration * 1000;
    let frameId;

    const tick = (now) => {
      const progress = Math.min((now - start) / durationMs, 1);
      const eased = 1 - (1 - progress) ** 3;
      const current = target * eased;
      setDisplay(decimals > 0 ? current : Math.round(current));
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [value, duration, decimals]);

  const formatted =
    decimals > 0 ? display.toFixed(decimals) : String(Math.round(display));

  return <span className={className}>{formatted}</span>;
}

function Homepage() {
  const [stats, setStats] = useState({ total: 0, open: 0, responses: 0, loading: true });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await axios.get(`${dbUrl}invitations.json`);
        const entries = res.data ? Object.entries(res.data) : [];
        let totalResponses = 0;
        let openCount = 0;

        await Promise.all(
          entries.map(async ([id, inv]) => {
            const count = await getResponseCount(id, inv.responses);
            totalResponses += count;
            if (isInvitationOpen(inv)) openCount += 1;
          })
        );

        setStats({
          total: entries.length,
          open: openCount,
          responses: totalResponses,
          loading: false,
        });
      } catch {
        setStats((s) => ({ ...s, loading: false }));
      }
    };
    loadStats();
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:py-12">
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-12 text-center"
      >
        <img src={logo} alt="Banco Alimentare della Daunia" className="mx-auto mb-6 max-h-24 object-contain lg:hidden" />
        <h1 className="text-3xl font-bold text-[var(--ba-body-text)] md:text-4xl">
          Gestore di inviti
        </h1>
        <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-brand-orange" />
        <p className="mt-2 text-lg font-semibold text-brand-green">Banco Alimentare della Daunia F. Vassalli</p>
        <p className="mx-auto mt-4 max-w-xl ba-muted">
          Crea inviti personalizzati per eventi e raccolte, condividili con un link e raccogli le risposte in un unico posto.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg">
            <Link to="/editor-invito">
              <PlusCircle className="h-4 w-4" />
              Crea nuovo invito
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/tutti-gli-inviti">
              <CalendarDays className="h-4 w-4" />
              Tutti gli inviti
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/eventi">
              <LayoutGrid className="h-4 w-4" />
              Eventi e turni
            </Link>
          </Button>
        </div>
      </motion.section>

      <section className="mb-12">
        <h2 className="mb-4 text-center text-xl font-bold">Panoramica</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="flex-row items-center gap-3 space-y-0 pb-2">
              <CalendarDays className="h-5 w-5 text-brand-orange" />
              <CardTitle className="text-base font-semibold">Inviti totali</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold tabular-nums">
                <CountUp value={stats.total} />
              </p>
              <p className="text-sm ba-muted">
                <CountUp value={stats.open} /> aperti
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex-row items-center gap-3 space-y-0 pb-2">
              <MessageSquare className="h-5 w-5 text-brand-green" />
              <CardTitle className="text-base font-semibold">Risposte ricevute</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold tabular-nums">
                <CountUp value={stats.responses} />
              </p>
              <p className="text-sm ba-muted">Su tutti gli inviti</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex-row items-center gap-3 space-y-0 pb-2">
              <Users className="h-5 w-5 text-brand-info" />
              <CardTitle className="text-base font-semibold">Media risposte</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold tabular-nums">
                <CountUp
                  value={stats.total ? stats.responses / stats.total : 0}
                  decimals={1}
                />
              </p>
              <p className="text-sm ba-muted">Per invito</p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="mb-6 text-center text-xl font-bold md:text-2xl">Come funziona</h2>
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-50px' }}
          className="grid gap-4 sm:grid-cols-2"
        >
          {steps.map((step, i) => (
            <motion.div key={step.title} variants={item}>
              <Card className="h-full transition-shadow hover:shadow-md">
                <CardHeader className="flex-row items-start gap-4 space-y-0">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 ${step.color}`}>
                    <step.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase text-[var(--ba-muted)]">Step {i + 1}</span>
                    <CardTitle className="mt-0.5">{step.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="ba-muted">{step.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section id="faq" className="scroll-mt-24">
        <h2 className="mb-4 text-center text-xl font-bold">Domande frequenti</h2>
        <Card>
          <CardContent className="pt-6">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => (
                <AccordionItem key={faq.q} value={`faq-${i}`}>
                  <AccordionTrigger>{faq.q}</AccordionTrigger>
                  <AccordionContent>{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

export default Homepage;
